import {
  AutoFixHigh,
  CameraAlt,
  Garage,
  CarRepair,
  Flare,
  AutoAwesomeMotion,
  LocalCarWash,
  Brush,
  PublishedWithChanges,
  FormatPaint,
  AutoAwesome,
  Album,
  ManageSearch,
  BlurCircular,
  EventSeat,
  PhotoFilter,
  Send,
  MoreVert,
  Calculate,
} from '@mui/icons-material';
import { Alert, IconButton, Stack, Icon, Box, Avatar } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep, AlertType } from '../../common/enums/enums';
import { GenCurrency, GenDate } from '../../components/GenDataFormat';
import { getShortOrderNumber } from '../../helpers/orders';
import { isReconditioning } from '../../helpers/reconditioning';
import WOrderTagMenu from './WOrderTagMenu';

const theme = createTheme({
  palette: {
    pending_approval: {
      border: '#0000003B',
      background: '#FAFAFA',
    },
    paused: {
      border: '#C77700',
      background: '#FAFAFA',
    },
  },
});
const capitalizeFirstLetter = word =>
  word.charAt(0).toUpperCase() + word.slice(1);
export const uniqueServicesOnly = services => [
  ...new Set(services?.map(data => data.type)),
];
export const formatTextServices = services => {
  const [firstService, secondService] = services;
  const str = ', (value+)';
  const extraServices =
    services.length - 2 > 0 ? str.replace(/value/i, services.length - 2) : '';
  return `${capitalizeFirstLetter(firstService)}, ${capitalizeFirstLetter(
    secondService
  )}${extraServices}`;
};

const getOrderTagIcon = (services, t) => {
  const orderTagIcons = {
    intake: { icon: <Garage />, textService: t('detail.orderTag.intake') },
    body_shop: {
      icon: <CarRepair />,
      textService: t('detail.orderTag.body_shop'),
    },
    glass_chip: {
      icon: <Flare />,
      textService: t('detail.orderTag.glass_chip'),
    },
    photos: { icon: <CameraAlt />, textService: t('detail.orderTag.photos') },
    wash_and_prep: {
      icon: <LocalCarWash />,
      textService: t('detail.orderTag.wash_and_prep'),
    },
    detail: { icon: <AutoFixHigh />, textService: t('detail.orderTag.detail') },
    touch_up: { icon: <Brush />, textService: t('detail.orderTag.touch_up') },
    final_quality_check: {
      icon: <PublishedWithChanges />,
      textService: t('detail.orderTag.final_quality_check'),
    },
    paint: { icon: <FormatPaint />, textService: t('detail.orderTag.paint') },
    buff: { icon: <AutoAwesome />, textService: t('detail.orderTag.buff') },
    wheel_repair: {
      icon: <Album />,
      textService: t('detail.orderTag.wheel_repair'),
    },
    inspection: {
      icon: <ManageSearch />,
      textService: t('detail.orderTag.inspection'),
    },
    paintless_dent_repair: {
      icon: <BlurCircular />,
      textService: t('detail.orderTag.paintless_dent_repair'),
    },
    interior_repair: {
      icon: <EventSeat />,
      textService: t('detail.orderTag.interior_repair'),
    },
    tint: { icon: <PhotoFilter />, textService: t('detail.orderTag.tint') },
    delivery: { icon: <Send />, textService: t('detail.orderTag.delivery') },
  };
  services = uniqueServicesOnly(services);
  if (services.length > 1) {
    return {
      icon: <AutoAwesomeMotion />,
      textService: formatTextServices(services),
    };
  }
  return orderTagIcons[services[0]];
};

const {
  GENERATED,
  APPROVED,
  IN_TRANSIT,
  ARRIVED,
  IN_PROCESS,
  DONE,
  CANCELED,
  TRANSFERRED,
  DECLINED,
  PAUSED,
  PENDING_APPROVAL,
} = ProcessStep;

const getColors = step => {
  const { palette } = theme;
  const { Info, Warning, Success } = AlertType;

  switch (step) {
    case GENERATED:
      return {
        severity: Info,
        border_color: palette.info.light,
      };
    case APPROVED:
      return {
        severity: Info,
        border_color: palette.info.main,
      };
    case IN_TRANSIT:
      return {
        severity: Info,
        border_color: palette.info.dark,
      };
    case ARRIVED:
      return {
        severity: Info,
        border_color: palette.primary.dark,
      };
    case IN_PROCESS:
    case TRANSFERRED:
      return {
        severity: Warning,
        border_color: palette.warning.light,
      };
    case DONE:
      return {
        severity: Success,
        border_color: palette.success.light,
      };
    case CANCELED:
    case DECLINED:
      return {
        severity: AlertType.Error,
        border_color: palette.error.light,
      };
    case PAUSED:
      return {
        border_color: palette.paused.border,
        custom_background_color: palette.paused.background,
      };
    case PENDING_APPROVAL:
      return {
        border_color: palette.pending_approval.border,
        custom_background_color: palette.pending_approval.background,
        pending_approval_color: palette.warning.light,
      };
    default:
      return {
        severity: Info,
        border_color: palette.primary.light,
      };
  }
};

const showCompletionDateOnDone = (step, completedOn) => {
  return (
    <>
      {step} • <GenDate date={completedOn} noTime />
    </>
  );
};

const getStep = order => {
  if (order.step === ProcessStep.DONE && isReconditioning(order))
    return showCompletionDateOnDone(order.step, order.completedOn);
  return order.step;
};

const getStepPendingApproval = order =>
  order.requiresApproval ? PENDING_APPROVAL : getStep(order);

export default function WOrderTag({
  data,
  history,
  handleStartDone,
  addTechnician,
  hasActionsEnabled = true,
}) {
  const { t } = useTranslation('workOrdersTable');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const {
    step,
    services,
    woNumber,
    requiresApproval: pending_approval,
    vendorOrderId,
    amount: total_cost,
    orderType,
  } = data;
  const {
    severity,
    border_color,
    custom_background_color,
    pending_approval_color,
  } = getColors(pending_approval ? PENDING_APPROVAL : step);
  const { icon, textService } = getOrderTagIcon(services, t);
  const isEstimate = orderType === 'estimate';
  const servicesWithAssignee = services
    .filter(service => !service.declinedAt)
    .filter(service => service.assignee);
  const stepIsArrived = step === ProcessStep.ARRIVED;
  const stepIsInProcess = step === ProcessStep.IN_PROCESS;
  const stepIsInDone = step === ProcessStep.DONE;
  const stepIsInCanceledOrDeclined =
    step === ProcessStep.CANCELED || step === ProcessStep.DECLINED;
  const enableStartLink = servicesWithAssignee.length && stepIsArrived;
  const enableDoneLink = servicesWithAssignee.length && stepIsInProcess;

  const handleClickOnWorkOrder = () =>
    history?.push({
      pathname: `/orders/${vendorOrderId}`,
    });

  const handleMenuClick = action => {
    setAnchorEl(null);
    action();
  };

  const handleClickOnReconOrder = () => {
    if (!isReconditioning(data)) return;
    handleClickOnWorkOrder();
  };

  const handleClickOnNoReconOrder = () => {
    if (isReconditioning(data)) return;
    handleClickOnWorkOrder();
  };

  return (
    <Alert
      sx={{
        order: 1,
        '& .MuiAlert-icon': {
          alignSelf: 'center',
          marginRight: '4px',
          color: severity ?? border_color,
        },
        '& .MuiAlert-message': {
          padding: 0,
        },
        '& .MuiAlert-action': {
          padding: 0,
          marginRight: 0,
        },
        '&:hover': {
          cursor: !isReconditioning(data) ? 'pointer' : '',
        },
        border: `1px solid ${border_color}`,
        padding: '4px 4px 3px 4px',
        variant: 'filled',
        backgroundColor: custom_background_color,
      }}
      severity={severity}
      icon={icon}
      onClick={handleClickOnNoReconOrder}
      action={
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="end"
            pb={1}
          >
            <Typography variant="body1" color={'black'}>
              <GenCurrency value={total_cost} />
            </Typography>
            {hasActionsEnabled && (
              <>
                <IconButton
                  sx={{ color: 'rgba(0, 0, 0, 0.54)', padding: 0 }}
                  onClick={event => setAnchorEl(event.currentTarget)}
                  id={data?.orderId}
                  disabled={pending_approval || stepIsInCanceledOrDeclined}
                >
                  <MoreVert />
                </IconButton>
                <WOrderTagMenu
                  anchorEl={anchorEl}
                  open={open}
                  setAnchorEl={setAnchorEl}
                  data={data}
                  handleMenuClick={handleMenuClick}
                  addTechnician={addTechnician}
                  handleStartDone={handleStartDone}
                  stepIsInDone={stepIsInDone}
                  stepIsInProcess={stepIsInProcess}
                  pending_approval={pending_approval}
                  enableStartLink={enableStartLink}
                  enableDoneLink={enableDoneLink}
                />
              </>
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            onClick={handleClickOnWorkOrder}
            sx={{
              ':hover': {
                cursor: 'pointer',
              },
            }}
            justifyContent="end"
          >
            {servicesWithAssignee.length ? (
              <Box>
                <Avatar
                  alt={[
                    servicesWithAssignee[0]?.assignee.firstName,
                    servicesWithAssignee[0]?.assignee.lastName,
                  ]
                    .filter(x => x)
                    .join(' ')}
                  src={servicesWithAssignee[0]?.assignee.photo}
                  sx={{ width: '24px', height: '24px' }}
                />
              </Box>
            ) : (
              <>
                <Typography variant="caption">
                  {t('detail.orderTag.unassigned')}
                </Typography>
                <Icon>{isEstimate && <Calculate />}</Icon>
              </>
            )}
          </Stack>
        </Stack>
      }
    >
      <Stack
        onClick={handleClickOnReconOrder}
        sx={{
          ':hover': {
            cursor: 'pointer',
          },
        }}
      >
        <Typography
          variant="body2"
          fontWeight={'700'}
          color={'black'}
          title={`${t('detail.orderTag.workOrder')} ${woNumber}`}
          width={'165px'}
          noWrap
        >
          {t('detail.orderTag.workOrder')} {getShortOrderNumber(woNumber)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {textService}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: pending_approval_color ?? border_color }}
        >
          {getStepPendingApproval(data)}
        </Typography>
      </Stack>
    </Alert>
  );
}
